/**
 * Universal Text Reveal (Titles + Paragraphs)
 * - Headings (h1-h6): fade + translateY
 * - Paragraphs (p): simple fade
 * - Hero visible animation with delayed reveal
 * - Respects prefers-reduced-motion
 */

(() => {
  const DESKTOP_ONLY = false; // true = >= 992px uniquement
  const DESKTOP_QUERY = "(min-width: 992px)";

  const INITIAL_REVEAL_DELAY_MS = 120;
  const STAGGER_MS = 70;
  const MAX_STAGGER_ITEMS = 10;

  const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reducedMotion) return;
  if (DESKTOP_ONLY && !isDesktop) return;

  /* ------------------------------------------------------------------
     Inject CSS once
  ------------------------------------------------------------------ */
  if (!document.querySelector('[data-universal-text-reveal="true"]')) {
    const css = `
      .utr-heading {
        opacity: 0;
        transform: translate3d(0,14px,0);
        transition: opacity 520ms ease, transform 760ms cubic-bezier(.2,.8,.2,1);
        will-change: opacity, transform;
      }
      .utr-heading.is-revealed {
        opacity: 1;
        transform: translate3d(0,0,0);
      }

      .utr-paragraph {
        opacity: 0;
        transition: opacity 520ms ease;
        will-change: opacity;
      }
      .utr-paragraph.is-revealed {
        opacity: 1;
      }
    `;
    const style = document.createElement("style");
    style.setAttribute("data-universal-text-reveal", "true");
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------
     Helpers
  ------------------------------------------------------------------ */
  const $$ = (sel) => Array.from(document.querySelectorAll(sel));
  const mark = (el, cls) => el && !el.classList.contains(cls) && el.classList.add(cls);
  const reveal = (el) => el && !el.classList.contains("is-revealed") && el.classList.add("is-revealed");

  const isInView = (el, vh, vw) => {
    const r = el.getBoundingClientRect();
    return r.top < vh * 0.92 && r.bottom > 0 && r.left < vw && r.right > 0;
  };

  /* ------------------------------------------------------------------
     Zones exclues de l'animation.

     Un element anime demarre a opacity:0 et n'est revele que lorsqu'il
     entre dans le viewport. Dans un slider, un menu off-canvas ou une
     popup, l'element n'entre jamais dans le viewport au chargement :
     il resterait donc invisible une fois le conteneur ouvert ou fait
     defiler. Ces zones gardent leur affichage normal.
  ------------------------------------------------------------------ */
  const EXCLUDED = [
    ".swiper", ".swiper-slide", ".slideshow", ".slider", "[class*='carousel']",
    ".offcanvas", ".mobile-offcanvas-menu", ".drawer", ".cart-notification",
    ".modal", ".popup", ".quick-view", ".search__content", ".header",
    ".announcement-bar", ".scrolling-bar", ".marquee",
    "[hidden]", "[aria-hidden='true']", "template"
  ].join(",");

  const isExcluded = (el) => !!el.closest(EXCLUDED);

  /* ------------------------------------------------------------------
     Mark elements
  ------------------------------------------------------------------ */
  const headings = $$("h1, h2, h3, h4, h5, h6").filter(el => !isExcluded(el));
  const paragraphs = $$("p").filter(el => !isExcluded(el));

  headings.forEach(h => mark(h, "utr-heading"));
  paragraphs.forEach(p => mark(p, "utr-paragraph"));

  /* ------------------------------------------------------------------
     Observer for scroll reveals
  ------------------------------------------------------------------ */
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      const el = entry.target;
      el.style.transitionDelay = el.classList.contains("utr-paragraph") ? "25ms" : "0ms";
      reveal(el);
      io.unobserve(el);
    });
  }, {
    threshold: 0.12,
    rootMargin: "0px 0px -8% 0px"
  });

  $$(".utr-heading, .utr-paragraph").forEach(el => io.observe(el));

  /* ------------------------------------------------------------------
     Delayed reveal for hero (above-the-fold)
  ------------------------------------------------------------------ */
  const delayedInitialReveal = () => {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;

    const inViewItems = $$(".utr-heading, .utr-paragraph")
      .filter(el => !el.classList.contains("is-revealed"))
      .filter(el => isInView(el, vh, vw))
      .slice(0, MAX_STAGGER_ITEMS);

    inViewItems.forEach((el, idx) => {
      try { io.unobserve(el); } catch (e) {}
      el.style.transitionDelay = `${INITIAL_REVEAL_DELAY_MS + idx * STAGGER_MS}ms`;
      setTimeout(() => reveal(el), INITIAL_REVEAL_DELAY_MS + idx * STAGGER_MS);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      requestAnimationFrame(() => requestAnimationFrame(delayedInitialReveal));
    }, { once: true });
  } else {
    requestAnimationFrame(() => requestAnimationFrame(delayedInitialReveal));
  }

  window.addEventListener("load", () => setTimeout(delayedInitialReveal, 60), { once: true });

  /* ------------------------------------------------------------------
     Filet de securite

     Si un element n'a pas ete revele (onglet en arriere-plan au
     chargement, IntersectionObserver qui ne se declenche pas, element
     rendu visible plus tard par un script tiers), on l'affiche malgre
     tout. Un texte non anime reste lisible ; un texte invisible, non.
  ------------------------------------------------------------------ */
  const revealAll = () => $$(".utr-heading, .utr-paragraph").forEach(reveal);

  setTimeout(revealAll, 3000);
  window.addEventListener("pageshow", () => setTimeout(revealAll, 500), { once: true });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) setTimeout(delayedInitialReveal, 100);
  });

})();
